"use client";

import { MDXProvider } from "@mdx-js/react";
import { format } from "date-fns";
import { memo, Suspense } from "react";
import dynamic from "next/dynamic";
import Head from "next/head";
import { NextSeo, ArticleJsonLd } from "next-seo";
import Script from "next/script";
import Balancer from "react-wrap-balancer";
const CodeSection = dynamic(() => import("./CodeSection"), { ssr: false });
import { SocialLinks } from "../SocialLinks";
import { Navbar } from "../Navbar";
import { EnableTopherDisplayWhenScrolledPast } from "../../utils/useEnableTopherDisplay";
import { blogPostsBase } from "../../../blogposts";
import { OptimizedImage } from "../OptimizedImage";
import { FontPlayfair } from "../fonts/playfair";
import { usehasRenderedOnce } from "../../utils/useIsBrowser";
import { BrowserOnly } from "../BrowserOnly";
import { HeaderWithAnchor } from "./helpers";
import { RelatedArticles } from "./RelatedArticles";

export function Layout({ postId }: { postId: string }) {
  const postMeta = blogPostsBase.find((post) => post.id === postId)!;

  const { title, date } = postMeta;

  return function ({ children }: { children: React.ReactNode }) {
    const pageUrl = `https://topher.io/writing/${postMeta.slug}`;
    const heroImageUrl = `https://topher.io${postMeta.heroImage.default.src}`;

    return (
      <>
        <>
          <Head>{title} - Topher Winward</Head>
          <NextSeo
            title={title}
            description={postMeta.description}
            canonical={pageUrl}
            openGraph={{
              title: title,
              description: postMeta.description,
              url: pageUrl,
              type: "article",
              article: {
                publishedTime: postMeta.date.toISOString(),
                tags: postMeta.tags,
              },
              images: [
                {
                  url: heroImageUrl,
                },
              ],
            }}
            twitter={{ handle: "@winwardo" }}
          />
          <ArticleJsonLd
            url={pageUrl}
            title={title}
            images={[heroImageUrl]}
            publisherName={"Topher Winward"}
            publisherLogo={`https://topher.io${
              require("../../../public/favicon-32x32.png").default.src
            }`}
            datePublished={postMeta.date.toISOString()}
            dateModified={postMeta.date.toISOString()}
            authorName="Topher Winward"
            description={postMeta.description}
          />
        </>
        <div>
          <Navbar />
          <header className="container px-3 mx-auto">
            <>
              <FontPlayfair>
                <h1
                  style={{ fontSize: "64" }}
                  className="pt-2 text-5xl leading-tight tracking-tight md:text-6xl md:text-center md:pt-8 md:mb-6 md:px-16 lg:px-24"
                >
                  <Balancer>{title}</Balancer>
                </h1>
              </FontPlayfair>
              <div className="text-2xl font-semibold text-gray-600 blog-wrapper">
                <Balancer>{postMeta.description}</Balancer>
              </div>
              <div className="my-4 md:my-6 md:px-32">
                <div className="w-full border-b border-black border-opacity-25" />
              </div>
            </>
            <PostedByTopher date={date} />
          </header>
          <main>
            <article className="mx-auto mb-12 text-lg blog-wrapper">
              <MDXWrapper>{children}</MDXWrapper>
            </article>
          </main>
          <aside className="mx-auto mb-32 text-lg blog-wrapper">
            <HeaderWithAnchor id="donation" number={998}>
              While you're here... 🌟
            </HeaderWithAnchor>
            <Paragraph>
              A little support can go a long way. Donations from kind people as
              yourself help me continue to independently publish articles like
              this one, with all the effort and production that goes into it.
            </Paragraph>
            <Paragraph>
              Either way, thank you for reading, and I hope you learned
              something helpful!
            </Paragraph>
            <div className="flex justify-center py-4">
              <Donations />
            </div>
          </aside>
          <footer>
            <RelatedArticles postId={postId} />
          </footer>
        </div>
      </>
    );
  };
}

function Donations() {
  return (
    <>
      <div>
        <a
          href="https://ko-fi.com/M4M5KE52Q"
          target="_blank"
          className="flex gap-x-2"
        >
          <OptimizedImage
            src={require("../../images/topher.jpg")}
            alt="Photo of Topher Winward"
            width={36}
            height={36}
            loading="lazy"
            className="bg-green-800 rounded-full"
          />

          <img
            height="36"
            style={{ border: "0px", height: "36px" }}
            src="https://storage.ko-fi.com/cdn/kofi2.png?v=3"
            alt="Buy Me a Coffee at ko-fi.com"
          />
        </a>
      </div>
      <BrowserOnly>
        <Script
          src="https://storage.ko-fi.com/cdn/scripts/overlay-widget.js"
          onLoad={() => {
            try {
              // @ts-ignore
              const kofi = window.kofiWidgetOverlay;
              kofi.draw("topherwinward", {
                type: "floating-chat",
                "floating-chat.donateButton.text": "Support Me",
                // 'floating-chat.donateButton.background-color': '#ff38b8',
                "floating-chat.donateButton.background-color":
                  "rgb(246, 135, 179)",
                "floating-chat.donateButton.text-color": "#fff",
              });
            } catch (e) {
              console.warn("Kofi failed to load", e);
            }
          }}
        />
      </BrowserOnly>
    </>
  );
}

function PostedByTopher({ date }) {
  return (
    <>
      <EnableTopherDisplayWhenScrolledPast />
      <PostedByTopherInner date={date} />
    </>
  );
}

const PostedByTopherInner = memo<{ date: Date }>(function PostedByTopherInner({
  date,
}) {
  const postDate = format(date, "MMM do, yyyy");
  return (
    <div className="flex flex-col w-full px-2 mb-8 md:items-center sm:flex-row md:justify-between md:px-24 lg:px-48 md:mb-8">
      <div className="flex items-center">
        <div className="w-20 h-20 md:h-16 md:w-16">
          <OptimizedImage
            src={require("../../images/topher.jpg")}
            alt="Photo of Topher Winward"
            width={64}
            height={64}
            loading="eager"
            className="bg-green-800 rounded-full"
          />
        </div>
        <div className="flex flex-col justify-center ml-4 md:ml-8">
          <div className="text-lg font-semibold">Topher Winward</div>
          <div className="text-sm text-gray-600">Posted {postDate}</div>
          <div className="md:hidden">
            <SocialLinks />
          </div>
        </div>
      </div>
      <div className="hidden md:block">
        <SocialLinks />
      </div>
    </div>
  );
});

export function Header1({ children, ...props }) {
  return (
    <h1
      {...props}
      children={children}
      className="mt-8 text-3xl font-bold text-center"
    />
  );
}

export function Header2({ children, ...props }) {
  return (
    <h2 {...props} className="mt-4 text-5xl">
      {children}
    </h2>
  );
}

export function Header3({ children, ...props }) {
  return <h3 {...props}>{children}</h3>;
}

export function Header4({ children, ...props }) {
  return <h4 {...props} children={children} className="mt-12 text-3xl" />;
}

export function Header5({ children, ...props }) {
  return <h5 {...props} children={children} className="mt-4 text-2xl" />;
}
export function Header6({ children, ...props }) {
  return <h6 {...props} children={children} className="mt-4 text-xl" />;
}
export function UnorderedList({ children, ...props }) {
  return <ul {...props} children={children} className="ml-4 list-disc" />;
}
export function OrderedList({ children, ...props }) {
  return <ol {...props} children={children} className="ml-4 list-decimal" />;
}
export function ListItem({ children, ...props }) {
  return <li {...props} children={children} className="mb-2 md:ml-4 lg:ml-8" />;
}
export function Blockquote({ children, ...props }) {
  return (
    <div className="">
      <blockquote
        {...props}
        children={children}
        className="px-6 py-2 m-4 overflow-x-auto font-serif text-xl bg-gray-100 rounded-lg card-shadow"
      />
    </div>
  );
}
export function Pre({ children, ...props }) {
  return (
    <pre
      {...props}
      children={children}
      className="px-2 py-1 m-2 whitespace-pre-wrap bg-gray-100 border rounded-lg"
    />
  );
}
export function Anchor({ children, ...props }) {
  return (
    <>
      <a
        {...props}
        target="_blank"
        rel="noopener"
        children={children}
        className="italic font-semibold underline __css_anchor"
      />
    </>
  );
}
export function Table({ children, ...props }) {
  return <table {...props} children={children} className="table-auto" />;
}

export function Paragraph({ children, textIndent = "2rem", ...props }) {
  return (
    <p
      {...props}
      style={{ textIndent: textIndent, color: "#222" }}
      children={children}
      className="my-4 whitespace-pre-wrap"
    />
  );
}

export function Strong({ children, ...props }) {
  return (
    <>
      <strong
        {...props}
        children={children}
        style={{ color: "hsl(10, 75%, 48%)" }}
        className="font-semibold"
      />
    </>
  );
}

export function Code({ children, className, ...props }) {
  const displayDynamic = usehasRenderedOnce();
  const fallback = (
    <Pre
      {...props}
      className={className}
      style={{ paddingTop: "15px", paddingBottom: "36px" }}
    >
      {children}
    </Pre>
  );

  if (!displayDynamic) {
    return fallback;
  }

  return (
    <Suspense fallback={fallback}>
      <CodeSection {...props} className={className}>
        {children}
      </CodeSection>
    </Suspense>
  );
}

export function InlineCode({ children, ...props }) {
  return (
    <>
      <code
        {...props}
        children={children}
        className="px-2 py-1 font-mono bg-gray-300 rounded-lg"
      />
    </>
  );
}

export const mdxComponents = {
  h1: Header1,
  h2: Header2,
  h3: Header3,
  h4: Header4,
  h5: Header5,
  h6: Header6,
  ul: UnorderedList,
  ol: OrderedList,
  li: ListItem,
  blockquote: Blockquote,
  // pre: Pre,
  code: Code,
  a: Anchor,
  table: Table,
  p: Paragraph,
  strong: Strong,
  inlineCode: InlineCode,
};

export function MDXWrapper({ children }) {
  return <MDXProvider components={mdxComponents}>{children}</MDXProvider>;
}
